// Single-resource envelope unwrapping.
//
// The Smithy `@Operation` refactor of the backend wraps every single-resource
// response in a one-key envelope named after the resource — e.g. GET/POST/PATCH
// `/api/admin/roles/{id}` returns `{"role": {...}}`, `/api/tenant` returns
// `{"tenant": {...}}`, `/api/admin/users/{alias}` returns `{"user": {...}}`.
// List endpoints keep their own `{items|roles|...: [...]}` shape and are NOT
// handled here.
//
// The SPA clients were written against the older *bare* shape (the object's
// fields at the top level). Returning the raw envelope left every field
// `undefined` — which crashed detail pages that dereference nested fields
// (e.g. `role.role_id.replace(...)`, `rule.group_match.type`) and blanked
// pages that just read flat fields (e.g. tenant settings).
//
// `unwrapEnvelope` is tolerant: if the response is already bare (the envelope
// key is absent) it returns the response unchanged, so a backend that later
// drops the envelope — or a mock that returns the bare object — keeps working.

/**
 * Unwraps a single-key resource envelope.
 *
 * @param resp the parsed response body
 * @param key  the envelope key the backend wraps the resource under
 *             (e.g. "role", "rule", "tenant", "user", "group")
 * @returns the unwrapped resource when `resp[key]` is an object; otherwise
 *          `resp` itself (already-bare / un-enveloped).
 */
export function unwrapEnvelope<T>(resp: T, key: string): T {
  if (resp !== null && typeof resp === "object") {
    const inner = (resp as Record<string, unknown>)[key];
    if (inner !== null && typeof inner === "object") {
      return inner as T;
    }
  }
  return resp;
}
